import { useEffect, useRef, useState } from "react";
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WaveSurfer from "wavesurfer.js";
import TimelinePlugin from 'wavesurfer.js/dist/plugins/timeline';
import RegionsPlugin from "wavesurfer.js/dist/plugins/regions";
import { differenceInMilliseconds } from "date-fns";
import { useSelector } from 'react-redux';
import { getFolderTreeCollapsed } from '../store/selectors/app.selector';

export const AudioPlayer = ({ 
  audioBlob = null as any,
  data = [] as any
}) => {
  const waveformRef = useRef<any>(null);
  const wavesurfer = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [playing, setPlay] = useState(false);
  const [loading, setLoading] = useState(true);
  const [volume, setVolume] = useState(1);
  const [isSticky, setIsSticky] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const isTreeCollapsed = useSelector(getFolderTreeCollapsed);
  const [stickyWidth, setStickyWidth] = useState('100%');


  const loadAudio = async () => {
    wavesurfer.current.loadBlob(audioBlob);
  };

  const formWaveSurferOptions = (ref: any) => ({
    container: ref,
    height: 150,
    waveColor: "#eee",
    progressColor: "#3c8ebd",
    cursorColor: "OrangeRed",
    cursorWidth: 2,
    barWidth: 3,
    barGap: 3,
    barRadius: 3,
    barHeight: 0.6,
    fillParent: true,
    mediaControls: false,
    autoplay: false,
    interact: true,
    dragToSeek: true,
    hideScrollbar: false,
    audioRate: 1,
    autoScroll: true,
    autoCenter: false,
    sampleRate: 14000,
    responsive: true,
    plugins: [
      TimelinePlugin.create({
        container: '#wave-timeline',
      }),
    ]
  });

  const getTimes = data?.segments?.flatMap((s: any) =>
    s.steps.map((st: any) => ({
      ...st,
      segmentName: s.name,
      segmentId: s.id,
    }))
  );

  const DiffInMilliSeconds = (start: any, end: any) =>
    Math.abs(differenceInMilliseconds(new Date(start), new Date(end)));

  const extractRegions = (audioData: any, duration: any) => {
    const minValue = 0.01;
    const minSilenceDuration = 0.1;
    const mergeDuration = 0.2;
    const scale = duration / audioData.length;
    const silentRegions = [];

    let start = 0, end = 0, isSilent = false;
    for (let i = 0; i < audioData.length; i++) {
      if (audioData[i] < minValue) {
        if (!isSilent) {
          start = i;
          isSilent = true;
        }
      } else if (isSilent) {
        end = i;
        isSilent = false;
        if (scale * (end - start) > minSilenceDuration) {
          silentRegions.push({ start: scale * start, end: scale * end });
        }
      }
    }

    const mergedRegions = [];
    let lastRegion = null;
    for (let i = 0; i < silentRegions.length; i++) {
      if (lastRegion && silentRegions[i].start - lastRegion.end < mergeDuration) {
        lastRegion.end = silentRegions[i].end;
      } else {
        lastRegion = silentRegions[i];
        mergedRegions.push(lastRegion);
      }
    }

    const regions = [];
    let lastEnd = 0;
    for (let i = 0; i < mergedRegions.length; i++) {
      regions.push({ start: lastEnd, end: mergedRegions[i].start });
      lastEnd = mergedRegions[i].end;
    }

    return regions;
  };

  const getMarker = (stepId: any, segmentId: any, segmentName: any, details: any, isUpper: boolean) => {
    const content = document.createElement('div');
    content.className = 'icon';

    content.onmouseover = (e: any) => {
      const tooltip = e.target.querySelector('#tooltiptext');
      if (tooltip) tooltip.style.visibility = "visible";
    };

    content.onmouseout = (e: any) => {
      const tooltip = e.target.querySelector('#tooltiptext');
      if (tooltip) tooltip.style.visibility = "hidden";
    };

    if (isUpper) {
      content.innerHTML = `
        <div>
          <svg viewBox="0 0 40 80" style="width: 11px; height: 22px; min-width: 11px; margin-right: 5px;">
            <polygon stroke="#979797" fill="#000" points="20 0 40 30 40 80 0 80 0 30" transform="rotate(180, 20 40)"></polygon>
          </svg>
          ID: ${stepId}
          <div style="visibility: hidden" id="tooltiptext">
            <div style="font-style: italic; color: blue">${segmentId} - ${segmentName}</div>
            <div>Details: ${details}</div>
          </div>
        </div>`;
    } else {
      content.innerHTML = `
        <div>
          <div style="visibility: hidden" id="tooltiptext">
            <div style="font-style: italic; color: blue">${segmentId} - ${segmentName}</div>
            <div>Details: ${details}</div>
          </div>
          <svg viewBox="0 0 40 80" style="width: 11px; height: 22px; min-width: 11px; margin-right: 5px;">
            <polygon stroke="#979797" fill="red" points="20 0 40 30 40 80 0 80 0 30"></polygon>
          </svg>
          ID: ${stepId}
        </div>`;
    }

    return content;
  };

  useEffect(() => {
    setPlay(false);
    const options = formWaveSurferOptions(waveformRef.current);

    if (audioBlob) {
      wavesurfer.current = WaveSurfer.create({ ...options });
      loadAudio();

      wavesurfer.current.on('decode', (duration: any) => {
        const decodedData = wavesurfer.current.getDecodedData();
        const wsRegions = wavesurfer.current.registerPlugin(RegionsPlugin.create());
        if (decodedData) {
          const regions = extractRegions(decodedData.getChannelData(0), duration);
          let preSeconds: number = regions[1]?.start || 0;

          getTimes?.forEach((time: any, index: number) => {
            if (['Speak', 'Listen', 'Listen Anything'].includes(time.stepType)) {
              preSeconds += index === 0 ? 0 : DiffInMilliSeconds(getTimes[index - 1].testResult?.endTime, time.testResult?.startTime) / 1000;

              const content = time.prompt || !time.messageToPlay
                ? getMarker(time.testStepId, time.segmentId, time.segmentName, time.prompt, true)
                : getMarker(time.testStepId, time.segmentId, time.segmentName, time.messageToPlay, false);

              wsRegions.addRegion({
                id: time.prompt || !time.messageToPlay ? 'up' : 'down',
                start: preSeconds + ((time.testResult?.voice?.latency || 0) / 1000) - 0.1,
                content: content,
                drag: false,
                color: 'transparent',
                style: 'border-left-width: 0px;border-right-width: 0px'
              });

              preSeconds += (time.testResult?.voice?.recordingDuration || 0 + (time.testResult?.voice?.latency || 0)) / 1000;
            }
          });
        }

        setLoading(false);
      });

      return () => wavesurfer.current.destroy();
    }
  }, [audioBlob]);

  useEffect(() => {
    if (playing) {
      wavesurfer.current?.play();
    } else {
      wavesurfer.current?.pause();
    }
  }, [playing]);

  const handlePlayPause = () => setPlay(!playing);

  const onVolumeChange = (e: any) => {
    const newVolume = +e.target.value;
    if (newVolume) {
      setVolume(newVolume);
      wavesurfer.current.setVolume(newVolume);
    }
  };

  useEffect(() => {

    const updateStickyWidth = () => {
      const adjustedWidth = isTreeCollapsed ? '1720px' : '1283px'; // Adjust this based on your gridItemSize logic
      setStickyWidth(adjustedWidth);
    };

    updateStickyWidth();
    window.addEventListener('resize', updateStickyWidth);

    return () => window.removeEventListener('resize', updateStickyWidth);
  }, [isTreeCollapsed]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight; // Total scrollable height
      const clientHeight = document.documentElement.clientHeight; // Visible height of the viewport
      let threshold = 0;
      // Set the threshold based on the initial scroll height and client height
      if (scrollHeight < 1450) {
        threshold = (scrollHeight - clientHeight) * 1.1; // Adjust threshold to 10% of the scrollable height
      } else {
        threshold = (scrollHeight - clientHeight) * 0.1; // Adjust threshold to 10% of the scrollable height
      }
  
      const shouldStick = window.scrollY > threshold; // Check if scrollY exceeds the threshold
      setIsSticky(shouldStick);
    };
  
    // Calculate threshold once when the component mounts (initial load)
    handleScroll();
  
    // Add event listeners
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll); // Recalculate on window resize
  
    // Cleanup on component unmount
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll); // Cleanup on unmount
    };
  }, []); // Empty dependency array ensures this effect only runs once on mount
  

  return (
    <div
      ref={containerRef}
      style={{
        position: isSticky && !isMinimized ? 'fixed' : 'relative',
        background: '#fff',
        zIndex: 1000,
        top: 0,
        padding: '12px 0px',
        width: isSticky && !isMinimized ? stickyWidth : '100%',
        //width: isSticky && !isMinimized ? '1283px' : '100%',
        boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)',
        borderTop: '1px solid #ddd',
        borderRadius: '8px',
      }}
    >
      <div
        id="waveform"
        style={{
          visibility: loading ? 'hidden' : 'visible',
          height: '100px',
        }}
        ref={waveformRef}
      />
      <div id="wave-timeline" />
      <div style={{ display: 'flex', alignItems: 'center', marginTop: '40px', gap: '10px' }}>
        {audioBlob && (
          <>
            {!playing ? (
              <PlayCircleOutlineIcon
                style={{ fontSize: 30, cursor: 'pointer',  color : '#006a68' }}
                onClick={handlePlayPause}
              />
            ) : (
              <PauseCircleOutlineIcon
                style={{ fontSize: 30, cursor: 'pointer' ,  color : '#006a68'}}
                onClick={handlePlayPause}
              />
            )}
            <a
              href={URL.createObjectURL(audioBlob)}
              download="audio-file.wav"
              style={{
                color: 'black',
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                cursor: 'pointer',
              }}
            >
              <DownloadIcon style={{ fontSize: 30 , color : '#006a68'}} />
            </a>
            <div
              onClick={() => setIsMinimized(!isMinimized)}
                style={{
                  cursor: "pointer",
                  position: "absolute",
                  right: "0px",
                  width: "30px",
                  height: "30px",
                  borderRadius: "50%",
                  borderColor: "#006a68",
                  backgroundColor: "#fff",
                  boxShadow: "0px 2px 4px rgba(175, 36, 36, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 1100,
                }}
              title={isMinimized ? 'Expand Player' : 'Restore Player'}
            >
            {isMinimized ? (
              <ExpandLessIcon style={{ fontSize: 24 ,  color : '#006a68'}} />
            ) : (
              <ExpandMoreIcon style={{ fontSize: 24,  color : '#006a68' }} />
              
            )}
          </div>

          </>
        )}
      </div>
    </div>
  );
};
